import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';
import type { DeepReadonly } from 'ts-essentials';

/* eslint-disable-next-line no-restricted-imports */
import type { Store } from '@jane/shared/models';

import { NotificationsService } from '../../services/notifications';
import { StoreSource } from '../../sources/store';
import type { CustomerThunkAction } from '../customerAction';
import { getCart } from './cart';

const verifyStoreError = (error: string): CustomerThunkAction => {
  return (dispatch) => {
    dispatch(verifyStoreFailure());
    return NotificationsService.error(error);
  };
};

type VerifyStoreOptions = {
  forceEmbed?: boolean;
  framelessEmbed?: boolean;
  isPartnerHosted?: boolean;
  operatorEmbed?: boolean;
};
export const verifyStore =
  ({
    storeId,
    options = {},
  }: {
    options?: VerifyStoreOptions;
    storeId: string | number;
  }): CustomerThunkAction =>
  (dispatch) => {
    dispatch(initiateVerifyStore({ storeId }));

    const { isPartnerHosted = false } = options;

    const disableShopping =
      window.location.search.includes('disableShopping=1');

    return StoreSource.get(storeId).then(
      (response) => {
        if (!response || !response.store) {
          return dispatch(verifyStoreError('Store not found'));
        }

        const { store } = response;
        const overriddenStore = {
          ...store,
          hide_prices: store.hide_prices || disableShopping,
        };

        dispatch(
          verifyStoreSuccess({
            store: overriddenStore,
            isPartnerHosted,
          })
        );

        dispatch(getCart());
      },
      (err) => dispatch(verifyStoreError(err))
    );
  };

export type EmbeddedAppState = DeepReadonly<{
  disableAuthFeatures: boolean;
  hasVerified: boolean;
  isPartnerHosted: boolean;
  isVerifying: boolean;
  partnerId: string | number | undefined;
  partnerName: string | undefined;
}>;

const getInitialState = (): EmbeddedAppState => ({
  partnerId: undefined,
  partnerName: undefined,
  hasVerified: false,
  isVerifying: false,
  isPartnerHosted: false,
  disableAuthFeatures: false,
});

const embeddedAppSlice = createSlice({
  name: 'embeddedApp',
  initialState: getInitialState(),
  reducers: {
    setDisableAuthFeatures: (state, action: PayloadAction<boolean>) => {
      return { ...state, disableAuthFeatures: action.payload };
    },
    setPartner: (
      state,
      action: PayloadAction<{ id: string | number; name: string }>
    ) => {
      return {
        ...state,
        partnerId: action.payload.id,
        partnerName: action.payload.name,
      };
    },
    initiateVerifyStore: (
      state,
      action: PayloadAction<{ storeId: string | number }>
    ) => {
      return { ...state, isVerifying: true, partnerId: action.payload.storeId };
    },
    verifyStoreSuccess: (
      state,
      action: PayloadAction<{ isPartnerHosted: boolean; store: Store }>
    ) => {
      const { store, isPartnerHosted } = action.payload;
      if (state.partnerId !== store.id) {
        return {
          ...state,
          isVerifying: false,
          partnerId: undefined,
        };
      }

      return {
        ...state,
        partnerName: store.name,
        isVerifying: false,
        hasVerified: true,
        isPartnerHosted,
      };
    },
    verifyStoreFailure: (state) => {
      return { ...state, isVerifying: false, partnerId: undefined };
    },
  },
  selectors: {
    selectEmbeddedApp: (state) => state,
  },
});

export const {
  reducer: embeddedAppReducer,
  actions: {
    setDisableAuthFeatures,
    setPartner,
    initiateVerifyStore,
    verifyStoreSuccess,
    verifyStoreFailure,
  },
  selectors: { selectEmbeddedApp },
} = embeddedAppSlice;

export type EmbeddedAppActions =
  | ReturnType<typeof initiateVerifyStore>
  | ReturnType<typeof setDisableAuthFeatures>
  | ReturnType<typeof setPartner>
  | ReturnType<typeof verifyStoreFailure>
  | ReturnType<typeof verifyStoreSuccess>;
